import { queryOptions, useQuery, UseQueryResult } from '@tanstack/react-query';
import { Application, AppStatus } from 'merchant-api/types';
import { unifiedApiClient } from './client';
import { getApplicationOpts } from './useGetApplication';
import { useRouteContext } from '@tanstack/react-router';

export interface useApplicationProps {
  isLoadingApplication: boolean;
  applicationData: Application | null | undefined;
  isErrorApplication: any;
  opportunityId: string | undefined;
  applicationId: string | undefined;
  partyId: string | undefined;
  isPartnerChannel: Boolean;
  isPAR: Boolean;
  query: UseQueryResult<Application | null>;
}

export function getCurrentAppOpts(statuses?: AppStatus[], strictStatus: boolean = false) {
  const normalizedStatuses = [...(statuses ?? [])].sort();
  const hasStatuses = !!statuses && statuses.length > 0;

  return queryOptions<Application | null>({
    queryKey: ['currentApplication', ...normalizedStatuses, strictStatus],
    queryFn: async () => {
      // `?status=` is repeated for each status; the unified route reads them with getAll('status').
      const { data } = await unifiedApiClient.GET(
        '/client/application/v1/current_application',
        hasStatuses ? ({ params: { query: { status: statuses } } } as never) : undefined,
      );
      const app = (data as unknown as Application | null) ?? null;

      if (strictStatus) return app;
      if (app) return app;
      if (!hasStatuses) return null;

      const { data: fallback } = await unifiedApiClient.GET('/client/application/v1/current_application');
      return (fallback as unknown as Application | null) ?? null;
    },
  });
}

export function useApplication(statuses?: AppStatus[], strictStatus: boolean = false): useApplicationProps {
  const { applicationId } = useRouteContext({ strict: false });
  const query = useQuery(applicationId ? getApplicationOpts(applicationId) : getCurrentAppOpts(statuses, strictStatus));
  const { data, error, isPending } = query;
  return {
    applicationData: error || isPending ? undefined : data && Object.keys(data).length ? data : null,
    opportunityId: data?.metadata?.sfOpportunityId,
    applicationId: data?.id,
    partyId: data?.sourcePartyId,
    isLoadingApplication: isPending,
    isErrorApplication: error,
    isPartnerChannel: data?.metadata?.channelCode === 'PARTNER_ISO',
    isPAR: data?.metadata?.referralSource === 'Pre-approved Renewal',
    query,
  };
}
export default useApplication;
